import { useEffect, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router';
import Logo from '@/components/Logo';
import { api, ApiError } from '@/lib/api';
import { PROVINCES, formatCodePostal, formatTelephone } from '@/lib/clientForm';
import { formatMoney } from '@/lib/utils';

// Style commun des champs du formulaire de complétion.
const CHAMP = 'border-input bg-background h-12 w-full rounded-lg border px-3 text-base';

/**
 * Page d'accueil PUBLIQUE du visiteur (sans session SuperShow), atteinte par le
 * QR code imprimé sur l'étiquette : le QR encode l'adresse de cette page AVEC le
 * numéro de client (?client=… ou ?c=…). Le visiteur choisit ensuite entre :
 *   • « Jeu de la plus grosse » → transmet son numéro au jeu (jeu.pasuper.xyz) ;
 *   • « Mes cadeaux, mes commandes » → consultation publique (API /api/public).
 *
 * Le jeu accepte les mêmes noms de paramètre (?client= / ?c=) : le QR existant
 * fonctionne sans réimpression.
 */

// Adresse réelle du jeu (fournie par l'équipe « whogotthebiggest »). Le numéro
// de client scanné y est ajouté en query : le jeu fait entrer directement les
// ateliers déjà connus, sinon bascule sur son écran de connexion.
const JEU_URL = 'https://jeu.pasuper.xyz/scan';

type Gift = {
  tierId: number;
  value: string;
  giftName: string;
  claimedCount: number;
  claimable: boolean;
};
type CadeauGroupe = {
  groupId: number;
  groupName: string;
  budget: string;
  spent: string;
  remaining: string;
  gifts: Gift[];
};
type Ligne = {
  lineCode: string;
  partNumber: string;
  description: string;
  qty: number;
  unitPrice: string;
  lineTotal: string;
};
type Commande = {
  id: number;
  number: string;
  submittedAt: string | null;
  total: string;
  lines: Ligne[];
};
type Coordonnees = {
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  email: string;
};
type Infos = {
  customer: {
    custNum: string;
    name: string;
    isWalkIn: boolean;
    profilComplet: boolean;
    sharedAccount: boolean;
  };
  coordonnees: Coordonnees | null;
  grandTotal: string;
  commandes: Commande[];
  cadeaux: CadeauGroupe[];
};

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function ConcoursTest() {
  const [params] = useSearchParams();
  // Accepte ?client= (ou l'abréviation ?c=) : le n° de client passé par le QR.
  const clientParam = (params.get('client') ?? params.get('c') ?? '').trim();

  const [numero, setNumero] = useState(clientParam);
  const [vue, setVue] = useState<'accueil' | 'infos'>('accueil');
  const [infos, setInfos] = useState<Infos | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  const num = numero.trim();
  const jeuHref = num ? `${JEU_URL}?client=${encodeURIComponent(num)}` : undefined;

  async function chargerInfos() {
    if (!num) return;
    setChargement(true);
    setErreur('');
    try {
      const data = await api<Infos>(`/api/public/customer/${encodeURIComponent(num)}`);
      setInfos(data);
    } catch (e) {
      setInfos(null);
      setErreur(
        e instanceof ApiError && e.status === 404
          ? 'Aucun client trouvé pour ce numéro.'
          : 'Impossible de charger vos informations pour le moment.',
      );
    } finally {
      setChargement(false);
    }
  }

  // Le manifeste PWA (start_url: "/") est fait pour l'app du STAFF sur tablette :
  // installé depuis la page concours, le raccourci du client ouvrirait "/" (la
  // connexion staff) au lieu de sa page. On retire donc le manifeste ici : un
  // « ajouter à l'écran d'accueil » gardera alors l'URL COURANTE, avec son
  // ?client=… (les métabalises Apple assurent l'ouverture plein écran).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const parent = link?.parentNode ?? null;
    link?.remove();
    return () => {
      if (link && parent) parent.appendChild(link);
    };
  }, []);

  // Dès qu'un numéro est fourni par le QR, on charge la fiche (surtout le NOM,
  // affiché sur l'accueil à la place du numéro). Les données servent aussi à la
  // vue « Mes cadeaux, mes commandes » sans re-télécharger.
  useEffect(() => {
    if (clientParam) void chargerInfos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientParam]);

  function ouvrirInfos() {
    if (!num) return;
    setVue('infos');
    // (Re)charge si on n'a rien, ou si le numéro saisi a changé depuis.
    if (infos?.customer.custNum !== num && !chargement) void chargerInfos();
  }

  // Chargement initial venant du QR : on attend la fiche avant d'afficher quoi que ce soit.
  const chargementInitial = !!clientParam && chargement && !infos;
  // Nouveau client (walk-in) dont la fiche est incomplète : il doit la compléter
  // AVANT d'accéder au concours. Une fiche complète entre directement.
  const besoinCompletion = !!infos && infos.customer.isWalkIn && !infos.customer.profilComplet;

  // Page consultative : on ne montre QUE les cadeaux réclamés (déjà reçus) et
  // disponibles (à réclamer maintenant) — pas ceux encore « à atteindre ». Les
  // fournisseurs sans aucun cadeau dans ces deux états sont masqués.
  const cadeauxGroupes = (infos?.cadeaux ?? [])
    .map((g) => ({
      ...g,
      gifts: g.gifts.filter((gift) => gift.claimedCount > 0 || gift.claimable),
    }))
    .filter((g) => g.gifts.length > 0);

  return (
    <>
      <div aria-hidden className="app-damier pointer-events-none fixed inset-0 -z-10" />
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 p-6">
        <div className="bg-card w-full space-y-5 rounded-2xl border p-6 shadow-sm">
          <Logo className="mx-auto h-12" />

          {chargementInitial ? (
            <p className="text-muted-foreground py-8 text-center text-sm">Chargement…</p>
          ) : besoinCompletion && infos ? (
            <CompleterFiche
              customer={infos.customer}
              coordonnees={infos.coordonnees}
              onDone={chargerInfos}
            />
          ) : infos?.customer.sharedAccount ? (
            // Compte COMMUN (« RABAIS ÉTUDIANT »…) : ni cadeaux ni concours.
            <div className="space-y-3 py-4 text-center">
              <h1 className="text-2xl font-bold">{infos.customer.name}</h1>
              <p className="text-muted-foreground text-sm">
                Ce numéro est un <b>compte commun</b>. Il ne participe pas aux cadeaux ni au
                concours.
              </p>
            </div>
          ) : vue === 'accueil' ? (
            <>
              <div className="text-center">
                <h1 className="text-2xl font-bold">Bienvenue au Super Show</h1>
                <p className="text-muted-foreground text-sm">Que souhaitez-vous faire ?</p>
              </div>

              {/* Le n° vient du QR ; s'il manque (accès direct), on le fait saisir.
                  Une fois la fiche chargée, on affiche le NOM du client (pas le n°). */}
              {num ? (
                <p className="text-center text-sm">
                  {infos ? (
                    <span className="text-foreground text-base font-bold">
                      {infos.customer.name}
                    </span>
                  ) : chargement ? (
                    <span className="text-muted-foreground">Chargement…</span>
                  ) : (
                    <span className="text-muted-foreground">
                      Client{' '}
                      <span className="text-foreground font-mono font-bold tabular-nums">
                        {num}
                      </span>
                    </span>
                  )}
                </p>
              ) : (
                <label className="block text-left">
                  <span className="mb-1.5 block text-sm font-medium">Numéro de client</span>
                  <input
                    value={numero}
                    onChange={(e) => setNumero(e.target.value)}
                    placeholder="Ex. 044059"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                    className="border-input bg-background focus:ring-ring h-12 w-full rounded-lg border px-3 text-lg focus:ring-2 focus:outline-none"
                  />
                </label>
              )}

              <div className="grid gap-3">
                {/* Bouton principal : le jeu. Désactivé tant qu'aucun numéro n'est saisi. */}
                <a
                  href={jeuHref}
                  aria-disabled={!jeuHref}
                  onClick={(e) => {
                    if (!jeuHref) e.preventDefault();
                  }}
                  className={`flex h-14 items-center justify-center gap-2 rounded-xl text-lg font-semibold ${
                    jeuHref
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground pointer-events-none opacity-50'
                  }`}
                >
                  🏁 Jeu de la plus grosse
                </a>

                <button
                  type="button"
                  onClick={ouvrirInfos}
                  disabled={!num}
                  className="border-input flex h-14 items-center justify-center gap-2 rounded-xl border text-lg font-semibold disabled:opacity-40"
                >
                  🎁 Mes cadeaux, mes commandes
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-5">
              <button
                type="button"
                onClick={() => setVue('accueil')}
                className="text-muted-foreground text-sm font-medium"
              >
                ← Retour
              </button>

              {chargement && <p className="text-muted-foreground text-center text-sm">Chargement…</p>}
              {erreur && (
                <p className="text-destructive rounded-lg border border-dashed p-3 text-center text-sm">
                  {erreur}
                </p>
              )}

              {infos && (
                <>
                  <div className="text-center">
                    <h2 className="text-xl font-bold">{infos.customer.name}</h2>
                    <p className="text-muted-foreground text-sm">Client {infos.customer.custNum}</p>
                  </div>

                  <div className="bg-primary/10 border-primary/20 rounded-xl border p-4 text-center">
                    <p className="text-muted-foreground text-xs uppercase">Total de vos achats</p>
                    <p className="text-2xl font-bold tabular-nums">{formatMoney(infos.grandTotal)}</p>
                  </div>

                  {/* Cadeaux */}
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">🎁 Cadeaux</h3>
                    {cadeauxGroupes.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        Aucun cadeau reçu ou disponible pour l'instant.
                      </p>
                    ) : (
                      cadeauxGroupes.map((g) => {
                        const nbRecu = g.gifts.filter((x) => x.claimedCount > 0).length;
                        const nbDispo = g.gifts.length - nbRecu;
                        return (
                          <details key={g.groupId} open className="group rounded-lg border">
                            {/* En-tête repliable : nom du fournisseur + résumé + chevron. */}
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 [&::-webkit-details-marker]:hidden">
                              <span className="font-medium">{g.groupName}</span>
                              <span className="flex items-center gap-2">
                                <span className="text-muted-foreground text-xs">
                                  {[
                                    nbDispo > 0 ? `${nbDispo} dispo` : null,
                                    nbRecu > 0 ? `${nbRecu} reçu` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </span>
                                <span className="text-muted-foreground transition-transform group-open:rotate-180">
                                  ▾
                                </span>
                              </span>
                            </summary>

                            <div className="border-t px-3 pt-2 pb-3">
                              <p className="text-muted-foreground mb-2 text-xs tabular-nums">
                                Solde restant : {formatMoney(g.remaining)}
                              </p>
                              <ul className="space-y-2">
                                {g.gifts.map((gift) => (
                                  <li
                                    key={gift.tierId}
                                    className="flex items-start justify-between gap-3"
                                  >
                                    <div className="min-w-0">
                                      <p className="text-sm leading-snug font-medium">
                                        {gift.giftName}
                                      </p>
                                      <p className="text-muted-foreground text-xs tabular-nums">
                                        seuil {formatMoney(gift.value)}
                                      </p>
                                    </div>
                                    {gift.claimedCount > 0 ? (
                                      <span className="shrink-0 rounded-full bg-green-600/15 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
                                        ✓ reçu{gift.claimedCount > 1 ? ` ×${gift.claimedCount}` : ''}
                                      </span>
                                    ) : (
                                      <span className="bg-primary/10 text-primary shrink-0 rounded-full px-2 py-0.5 text-xs font-medium">
                                        disponible
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </details>
                        );
                      })
                    )}
                  </section>

                  {/* Commandes */}
                  <section className="space-y-2">
                    <h3 className="text-sm font-semibold">🧾 Commandes</h3>
                    {infos.commandes.length === 0 ? (
                      <p className="text-muted-foreground text-sm">Aucune commande soumise.</p>
                    ) : (
                      infos.commandes.map((c) => (
                        <div key={c.id} className="rounded-lg border p-3">
                          <div className="flex items-baseline justify-between">
                            <span className="font-medium">Commande {c.number}</span>
                            <span className="font-bold tabular-nums">{formatMoney(c.total)}</span>
                          </div>
                          <p className="text-muted-foreground text-xs">{formatDate(c.submittedAt)}</p>
                          <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                            {c.lines.map((l, i) => (
                              <li key={i} className="flex justify-between gap-2">
                                <span className="truncate">
                                  {l.qty}× {l.description}
                                </span>
                                <span className="tabular-nums">{formatMoney(l.lineTotal)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    )}
                  </section>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

/**
 * Formulaire de complétion de la fiche d'un NOUVEAU client (walk-in) avant l'accès
 * au concours. Mêmes champs formatés que la création côté admin/rep. À l'envoi, la
 * fiche est enregistrée puis rechargée : une fois complète, le visiteur entre.
 */
function CompleterFiche({
  customer,
  coordonnees,
  onDone,
}: {
  customer: { custNum: string; name: string };
  coordonnees: Coordonnees | null;
  onDone: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    name: (customer.name ?? '').toUpperCase(),
    phone: formatTelephone(coordonnees?.phone ?? ''),
    address: (coordonnees?.address ?? '').toUpperCase(),
    city: (coordonnees?.city ?? '').toUpperCase(),
    province: (coordonnees?.province ?? '').toUpperCase(),
    postalCode: formatCodePostal(coordonnees?.postalCode ?? ''),
    email: (coordonnees?.email ?? '').toUpperCase(),
  });
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState('');

  const set = (champ: keyof typeof form, valeur: string) =>
    setForm((f) => ({ ...f, [champ]: valeur }));
  const enMaj = (champ: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    set(champ, e.target.value.toUpperCase());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setEnvoi(true);
    setErr('');
    try {
      await api(`/api/public/customer/${encodeURIComponent(customer.custNum)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          province: form.province.trim(),
          postalCode: form.postalCode.trim(),
          email: form.email.trim(),
        }),
      });
      await onDone(); // recharge la fiche → profil complet → accès au concours
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Enregistrement impossible.');
      setEnvoi(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4 text-left">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Complétez votre fiche</h1>
        <p className="text-muted-foreground text-sm">
          Quelques informations avant d'accéder au concours.
        </p>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium">Nom *</span>
        <input value={form.name} onChange={enMaj('name')} required className={CHAMP} />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium">Téléphone *</span>
        <input
          value={form.phone}
          onChange={(e) => set('phone', formatTelephone(e.target.value))}
          inputMode="tel"
          placeholder="(514) 555-1234"
          pattern="\(\d{3}\) \d{3}-\d{4}"
          title="Format : (514) 555-1234"
          required
          className={CHAMP}
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium">Adresse *</span>
        <input value={form.address} onChange={enMaj('address')} required className={CHAMP} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Ville *</span>
          <input value={form.city} onChange={enMaj('city')} required className={CHAMP} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Province *</span>
          <select
            value={form.province}
            onChange={(e) => set('province', e.target.value)}
            required
            className={CHAMP}
          >
            <option value="">—</option>
            {PROVINCES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block text-sm font-medium">Code postal *</span>
        <input
          value={form.postalCode}
          onChange={(e) => set('postalCode', formatCodePostal(e.target.value))}
          placeholder="H0H 0H0"
          pattern="[A-Z]\d[A-Z] \d[A-Z]\d"
          title="Format : H0H 0H0"
          required
          className={CHAMP}
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium">Courriel</span>
        <input
          value={form.email}
          onChange={enMaj('email')}
          inputMode="email"
          placeholder="NOM@DOMAINE.COM"
          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
          title="Adresse valide, ex. NOM@DOMAINE.COM"
          className={CHAMP}
        />
      </label>

      {err && <p className="text-destructive text-sm font-medium">{err}</p>}

      <button
        type="submit"
        disabled={envoi || !form.name.trim()}
        className="bg-primary text-primary-foreground h-12 w-full rounded-lg font-semibold disabled:opacity-40"
      >
        {envoi ? 'Enregistrement…' : 'Enregistrer et continuer'}
      </button>
    </form>
  );
}
